// client/src/components/Courses.js
// Student-facing courses component

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AnalyticsService from '../services/analyticsService';
import { filterContentForOrganization } from '../utils/organizationUtils';
import Header from './Header';
import CourseCard from './CourseCard';
import { BodyMedium } from './Typography';

const Courses = ({ onCourseClick, onNavigateToHome, onNavigateToProfile, onNavigateToPrompts, onNavigateToWritersRoom, onLogOut, onModeSwitch, showAdminMode = false }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseProgress, setCourseProgress] = useState({});
  const { getLevelProgress } = useProgress();
  const { user, userRole, currentSession, userOrganizations, loading: authLoading } = useAuth();
  const { themeColors } = useTheme();

  // Function to get course progress and status
  const getCourseProgress = useCallback(async (courseId) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      const courseQuery = query(
        lessonsRef,
        where('course_id', '==', courseId)
      );
      
      const courseSnapshot = await getDocs(courseQuery);
      const lessons = courseSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (lessons.length === 0) return { total: 0, completed: 0, hasActive: false, progress: 0 };
      
      // Count completed lessons
      const completedLessons = lessons.filter(lesson => {
        const lessonProgress = getLevelProgress(lesson.level, [lesson]);
        return lessonProgress?.isCompleted;
      });
      
      // Check if course has active lessons
      const hasActiveLessons = lessons.some(lesson => {
        const lessonProgress = getLevelProgress(lesson.level, [lesson]);
        return lessonProgress?.isActive;
      });
      
      const total = lessons.length;
      const completed = completedLessons.length;
      const progress = total > 0 ? (completed / total) * 100 : 0;
      
      return { total, completed, hasActive: hasActiveLessons, progress };
    } catch (error) {
      console.error('Error getting course progress:', error);
      return { total: 0, completed: 0, hasActive: false, progress: 0 };
    }
  }, [getLevelProgress]);

  // Fetch progress for all courses
  const fetchAllCourseProgress = useCallback(async () => {
    if (courses.length === 0 || !user) return;
    
    const progressData = {};
    for (const course of courses) {
      progressData[course.id] = await getCourseProgress(course.id);
    }
    setCourseProgress(progressData);
  }, [courses, getCourseProgress, user]);

  useEffect(() => {
    const fetchCourses = async () => {
      // Don't fetch courses until auth is fully loaded and user is authenticated
      if (authLoading || !user || !userRole) {
        return;
      }

      try {
        setLoading(true);
        
        const coursesRef = collection(db, 'courses');
        const snapshot = await getDocs(coursesRef);
        
        if (snapshot.empty) {
          // If no courses exist, create sample courses
          await createSampleCourses();
          fetchCourses(); // Fetch again after creating
        } else {
          const coursesList = filterContentForOrganization(
            snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })),
            userOrganizations
          );
          setCourses(coursesList);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setLoading(false);
      }
    };

    fetchCourses();
  }, [authLoading, user, userRole]); // Depend on auth state

  useEffect(() => {
    if (courses.length > 0 && user && userRole) {
      fetchAllCourseProgress();
    }
  }, [courses, fetchAllCourseProgress, user, userRole]);

  const createSampleCourses = async () => {
    const sampleCourses = [
      {
        course_name: 'Novel',
        course_headline: 'To tell stories is to be human.',
        course_description: 'Learn the essential skills and techniques to craft your own novel, from developing compelling characters to structuring an engaging plot. Whether you\'re a beginner or looking to refine your writing, this course will guide you through each step of the creative process to help bring your story to life.',
        course_id: 'STORYKIN_NOVEL',
        hours_to_complete: 110,
        lessons_count: 16,
        activities_count: 12,
        type: 'HOW TO WRITE A',
        title: 'Novel',
        createdAt: serverTimestamp()
      },
      {
        course_name: 'Short Story',
        course_headline: 'Master the art of concise storytelling.',
        course_description: 'Discover how to create powerful short stories that pack a punch. Learn to develop compelling characters, build tension, and craft satisfying endings in a compact format.',
        course_id: 'STORYKIN_SHORT_STORY',
        hours_to_complete: 60,
        lessons_count: 12,
        activities_count: 8,
        type: 'HOW TO WRITE A',
        title: 'Short Story',
        createdAt: serverTimestamp()
      },
      {
        course_name: 'Memoir',
        course_headline: 'Write your life story with compelling narrative.',
        course_description: 'Transform your life experiences into a compelling memoir. Learn to structure your personal story, develop your voice, and create emotional connections with readers.',
        course_id: 'STORYKIN_MEMOIR',
        hours_to_complete: 80,
        lessons_count: 14,
        activities_count: 10,
        type: 'HOW TO WRITE A',
        title: 'Memoir',
        createdAt: serverTimestamp()
      },
      {
        course_name: 'Personal Essay',
        course_headline: 'Craft personal essays that resonate with readers.',
        course_description: 'Learn to write personal essays that connect with readers on a deep level. Master the art of vulnerability, reflection, and storytelling in essay form.',
        course_id: 'STORYKIN_PERSONAL_ESSAY',
        hours_to_complete: 50,
        lessons_count: 10,
        activities_count: 6,
        type: 'HOW TO WRITE A',
        title: 'Personal Essay',
        createdAt: serverTimestamp()
      },
      {
        course_name: 'Publish',
        course_headline: 'Navigate the publishing world and get your work out there.',
        course_description: 'Learn the ins and outs of the publishing industry. From query letters to book proposals, discover how to get your work published and reach your audience.',
        course_id: 'STORYKIN_PUBLISH',
        hours_to_complete: 40,
        lessons_count: 8,
        activities_count: 5,
        type: 'HOW TO',
        title: 'Publish',
        createdAt: serverTimestamp()
      }
    ];

    try {
      const coursesRef = collection(db, 'courses');
      for (const course of sampleCourses) {
        await addDoc(coursesRef, course);
      }
      // Sample courses created successfully
    } catch (error) {
      console.error('Error creating sample courses:', error);
    }
  };

  const handleCourseClick = async (course) => {
    // Track course access analytics
    if (user && currentSession && userOrganizations) {
      await AnalyticsService.trackAccess(
        'course',
        course.id,
        currentSession,
        user.uid,
        userOrganizations,
        {
          courseTitle: course.title || 'Untitled Course',
          courseDescription: course.description || 'No description available',
          action: 'course_clicked'
        }
      );
    }
    
    if (onCourseClick) {
      onCourseClick(course);
    }
  };

  return (
    <div>
      {/* Header */}
      <Header 
        title="Courses" 
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={() => {}} // No-op when already on courses
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto pt-4 md:pt-6 pb-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-100px)] flex flex-col justify-center">
        {/* Loading State */}
        {(authLoading || loading) && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: themeColors.textPrimary }}></div>
              {/* role="status" announces loading message to screen readers */}
              <div role="status" aria-live="polite" className="mt-4">
                <BodyMedium style={{ color: themeColors.textPrimary }}>
                  {authLoading ? 'Loading...' : 'Loading courses...'}
                </BodyMedium>
              </div>
            </div>
          </div>
        )}

        {/* Course Content - Only show when not loading */}
        {!authLoading && !loading && (
          <>
            {/* Hero Text Section */}
            <div className="text-center pt-6 md:pt-8 mb-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black mb-3 max-w-4xl mx-auto" style={{fontFamily: 'Poppins, sans-serif', fontWeight: '900', lineHeight: '1.2', color: themeColors.textPrimary}}>
                What would you like to learn today?
              </h1>
            </div>

            {/* Staggered Course Grid - Hidden on mobile */}
        <div className="relative hidden lg:block">
          {/* First Row - 3 courses centered */}
          <div className="flex justify-center items-start space-x-6 mb-6">
            {courses.slice(0, 3).map((course) => (
              <CourseCard 
                key={course.id} 
                course={course} 
                onClick={handleCourseClick}
                progressData={courseProgress[course.id]}
              />
            ))}
          </div>

          {/* Second Row - 2 courses in gaps */}
          <div className="flex justify-center items-start space-x-6">
            {courses.slice(3, 5).map((course) => (
              <CourseCard 
                key={course.id} 
                course={course} 
                onClick={handleCourseClick}
                progressData={courseProgress[course.id]}
              />
            ))}
          </div>
        </div>

            {/* Responsive grid for mobile and tablet */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center lg:hidden">
              {courses.map((course) => (
                <CourseCard 
                  key={course.id} 
                  course={course} 
                  onClick={handleCourseClick}
                  progressData={courseProgress[course.id]}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Courses;
